/**
 * 组织架构管理页面
 * 布局：左侧组织树 + 右侧详情/成员面板
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Tag,
  Tabs,
  Form,
  Empty,
  Spin,
  type TreeDataNode,
} from 'antd';
import { message } from '@/utils/antdStatic';
import { modal } from '@/utils/antdStatic';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ExportOutlined,
  ApartmentOutlined,
  TeamOutlined,
  BankOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
} from '@ant-design/icons';
import { SearchForm } from '@/components/common/SearchForm';
import {
  organizationApi,
  OrganizationType,
  type Organization,
  type OrganizationQueryParams,
} from '@/api/modules/organization';
import type { RelationType } from '@/api/organization';
import { buildTreeData } from '@/utils/treeUtils';
import { OrganizationDetail, OrganizationForm, OrganizationMember } from './components/org';
import OrganizationTree from '@/components/OrganizationTree';
import type { DragInfo } from '@/components/OrganizationTree';

// ===== helpers =====

const ORG_TYPE_CONFIG: Record<number, { text: string; color: string; bg: string; icon: React.ReactNode }> = {
  [OrganizationType.COMPANY]:    { text: '公司', color: '#722ed1', bg: '#f9f0ff', icon: <BankOutlined /> },
  [OrganizationType.DEPARTMENT]: { text: '部门', color: '#1677ff', bg: '#e6f4ff', icon: <ApartmentOutlined /> },
  [OrganizationType.GROUP]:      { text: '小组', color: '#52c41a', bg: '#f6ffed', icon: <TeamOutlined /> },
  [OrganizationType.TEAM]:       { text: '团队', color: '#fa8c16', bg: '#fff7e6', icon: <TeamOutlined /> },
};

const ORG_TYPE_TO_RELATION_TYPE: Record<number, RelationType> = {
  [OrganizationType.COMPANY]: 'headquarters',
  [OrganizationType.DEPARTMENT]: 'division',
  [OrganizationType.GROUP]: 'region',
  [OrganizationType.TEAM]: 'store',
};

const RELATION_TYPE_TO_ORG_TYPE: Partial<Record<RelationType, OrganizationType>> = {
  headquarters: OrganizationType.COMPANY,
  subsidiary: OrganizationType.COMPANY,
  branch: OrganizationType.COMPANY,
  division: OrganizationType.DEPARTMENT,
  region: OrganizationType.GROUP,
  store: OrganizationType.TEAM,
  direct_store: OrganizationType.TEAM,
};

const getRelationTypes = (org?: Organization | null): RelationType[] => {
  if (!org) return [];
  return ((org as any).relationTypes ?? (org as any).relation_types ?? []) as RelationType[];
};

const getOrgType = (org?: Organization | null): OrganizationType => {
  const explicitType = Number((org as any)?.type);
  if (ORG_TYPE_CONFIG[explicitType]) return explicitType as OrganizationType;

  const relationType = getRelationTypes(org)[0];
  return RELATION_TYPE_TO_ORG_TYPE[relationType] ?? OrganizationType.DEPARTMENT;
};

const buildOrganizationPayload = (values: any) => {
  const relationTypes = getRelationTypes(values);
  const mappedRelationType = ORG_TYPE_TO_RELATION_TYPE[Number(values.type)] ?? 'division';
  const parentId = values.parent_id && String(values.parent_id) !== '0'
    ? Number(values.parent_id)
    : undefined;

  return {
    parent_id: parentId,
    name: values.name,
    code: values.code,
    relation_types: relationTypes.length > 0 ? relationTypes : [mappedRelationType],
    contact_phone: values.phone,
    contact_email: values.email,
    address: values.address,
    remark: values.description,
    sort_order: values.sort ?? 0,
    status: values.status === 0 || values.status === 'inactive' ? 'inactive' : 'active',
  };
};

/** 递归展平树结构为 flat list */
const flattenTree = (orgs: Organization[]): Organization[] =>
  orgs.flatMap(org => [org, ...flattenTree((org as any).children || [])]);

// ===== component =====

const OrganizationList: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [flatOrgs, setFlatOrgs] = useState<Organization[]>([]);
  const [orgTreeData, setOrgTreeData] = useState<TreeDataNode[]>([]);     // for form TreeSelect
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'members'>('members');
  const [treeExpandedKeys, setTreeExpandedKeys] = useState<string[]>([]);
  const [treeCollapsed, setTreeCollapsed] = useState(false);              // left panel collapse
  const [orgStats, setOrgStats] = useState({ total: 0, members: 0, depts: 0, teams: 0 });
  const [searchParams, setSearchParams] = useState<OrganizationQueryParams>({});

  // form state
  const [formVisible, setFormVisible] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [formOrg, setFormOrg] = useState<Organization | null>(null);
  const [form] = Form.useForm();

  // ── fetch ──────────────────────────────────────────────────────────────────

  const fetchOrgTree = useCallback(async () => {
    setLoading(true);
    try {
      const res = await organizationApi.getOrganizationTree({ includeMembers: true });
      const treeData: Organization[] = Array.isArray(res.data)
        ? res.data
        : (res.data as any)?.data ?? [];

      const flat = flattenTree(treeData);
      setFlatOrgs(flat);

      // TreeSelect data for form (needs flat list)
      setOrgTreeData(buildTreeData(flat));

      // default expand first level
      const roots = flat
        .filter(o => { const pid = (o as any).parentId ?? o.parent_id; return !pid || String(pid) === '0'; })
        .map(o => String(o.id));
      setTreeExpandedKeys(roots);

      // stats
      setOrgStats({
        total:   flat.length,
        members: flat.reduce((s, o) => s + ((o as any).memberCount ?? o.member_count ?? 0), 0),
        depts:   flat.filter(o => getOrgType(o) === OrganizationType.DEPARTMENT).length,
        teams:   flat.filter(o => getOrgType(o) === OrganizationType.TEAM).length,
      });
    } catch {
      message.error('获取组织架构失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrgTree(); }, [fetchOrgTree]);

  // ── tree actions ──────────────────────────────────────────────────────────

  const handleTreeSelect = useCallback((org: Organization) => {
    setSelectedOrg(org);
    setActiveTab('members');
  }, []);

  const handleTreeDrop = useCallback(async (info: DragInfo) => {
    const { dragNode, node, dropToGap, dropPosition } = info;
    const dragOrg = dragNode.organization;
    const targetOrg = node.organization;
    try {
      let targetParentId: string | undefined;
      let targetSort: number;
      if (dropToGap) {
        targetParentId = ((targetOrg as any).parentId ?? targetOrg.parent_id) as string | undefined;
        const targetOrgSort = (targetOrg as any).sort ?? (targetOrg as any).sort_order ?? 0;
        targetSort = dropPosition > 0 ? targetOrgSort + 1 : targetOrgSort;
      } else {
        targetParentId = String(targetOrg.id);
        const siblings = flatOrgs.filter(
          o => String((o as any).parentId ?? o.parent_id) === String(targetOrg.id)
        );
        targetSort = siblings.length > 0
          ? Math.max(...siblings.map(c => (c as any).sort ?? (c as any).sort_order ?? 0)) + 1
          : 1;
      }
      await organizationApi.moveOrganization(String(dragOrg.id), targetParentId, targetSort);
      message.success('移动成功');
      fetchOrgTree();
    } catch {
      message.error('移动失败');
    }
  }, [flatOrgs, fetchOrgTree]);

  const expandAll = () => setTreeExpandedKeys(flatOrgs.map(o => String(o.id)));
  const collapseAll = () => setTreeExpandedKeys([]);

  // ── form actions ──────────────────────────────────────────────────────────

  const handleOpenForm = (mode: 'create' | 'edit', org?: Organization, parentId?: string) => {
    setFormMode(mode);
    setFormOrg(org || null);
    setFormVisible(true);
    form.resetFields();
    if (mode === 'edit' && org) {
      form.setFieldsValue({
        parent_id: (org as any).parentId ?? org.parent_id,
        name: org.name,
        code: org.code,
        type: getOrgType(org),
        sort: (org as any).sort ?? (org as any).sort_order ?? 0,
        leader_id: (org as any).leaderId ?? org.leader_id,
        description: org.description ?? (org as any).remark,
        address: org.address,
        phone: org.phone ?? (org as any).contact_phone,
        email: org.email ?? (org as any).contact_email,
        status: org.status === 'inactive' ? 'inactive' : 'active',
      });
    } else if (parentId) {
      form.setFieldValue('parent_id', parentId);
    }
  };

  const handleSaveOrg = async () => {
    try {
      const values = await form.validateFields();
      const parentOrg = flatOrgs.find(o => String(o.id) === String(values.parent_id));
      const level = parentOrg ? parentOrg.level + 1 : 1;
      const payload = buildOrganizationPayload(values);
      if (formMode === 'create') {
        await organizationApi.createOrganization({ ...payload, level } as any);
        message.success('组织创建成功');
      } else {
        await organizationApi.updateOrganization(formOrg!.id, { ...payload, level } as any);
        message.success('组织更新成功');
        // keep selection up-to-date
        if (selectedOrg && selectedOrg.id === formOrg!.id) {
          setSelectedOrg(prev => prev ? { ...prev, ...values, ...payload, level } : null);
        }
      }
      setFormVisible(false);
      fetchOrgTree();
    } catch {
      // validation errors handled by form
    }
  };

  const handleDelete = (org: Organization) => {
    const hasChildren = flatOrgs.some(
      o => String((o as any).parentId ?? o.parent_id) === String(org.id)
    );
    modal.confirm({
      title: '确认删除组织',
      content: hasChildren
        ? `「${org.name}」下有子组织，删除后将一并删除所有子组织，确定继续？`
        : `确定要删除组织「${org.name}」吗？`,
      okType: 'danger',
      onOk: async () => {
        await organizationApi.deleteOrganization(org.id);
        message.success('删除成功');
        if (selectedOrg?.id === org.id) setSelectedOrg(null);
        fetchOrgTree();
      },
    });
  };

  // ── search ────────────────────────────────────────────────────────────────

  const searchFields = [
    { name: 'name',   label: '组织名称', type: 'input'  as const, placeholder: '请输入组织名称' },
    { name: 'code',   label: '组织编码', type: 'input'  as const, placeholder: '请输入组织编码' },
    {
      name: 'type', label: '组织类型', type: 'select' as const,
      placeholder: '请选择组织类型',
      options: [
        { label: '公司', value: OrganizationType.COMPANY },
        { label: '部门', value: OrganizationType.DEPARTMENT },
        { label: '小组', value: OrganizationType.GROUP },
        { label: '团队', value: OrganizationType.TEAM },
      ],
    },
    {
      name: 'status', label: '状态', type: 'select' as const,
      placeholder: '请选择状态',
      options: [
        { label: '正常', value: 'active' },
        { label: '停用', value: 'inactive' },
      ],
    },
  ];

  // ── render helpers ────────────────────────────────────────────────────────

  const typeConf = selectedOrg
    ? ORG_TYPE_CONFIG[getOrgType(selectedOrg)] || ORG_TYPE_CONFIG[OrganizationType.DEPARTMENT]
    : null;

  const tabItems = selectedOrg
    ? [
        {
          key: 'info',
          label: '基本信息',
          children: (
            <div style={{ padding: '16px 20px' }}>
              <OrganizationDetail org={selectedOrg} />
            </div>
          ),
        },
        {
          key: 'members',
          label: (
            <>
              成员管理
              <span style={{
                marginLeft: 6,
                background: '#1677ff',
                color: '#fff',
                fontSize: 10,
                padding: '0 5px',
                borderRadius: 8,
                lineHeight: '16px',
              }}>
                {(selectedOrg as any).memberCount ?? selectedOrg.member_count ?? 0}
              </span>
            </>
          ),
          children: (
            <div style={{ padding: '16px 20px' }}>
              <OrganizationMember org={selectedOrg} />
            </div>
          ),
        },
      ]
    : [];

  // ── JSX ──────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: 24 }}>

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {[
          { label: '组织总数',  value: orgStats.total,   color: '#1677ff', icon: <ApartmentOutlined /> },
          { label: '总人数',    value: orgStats.members, color: '#52c41a', icon: <TeamOutlined /> },
          { label: '部门数',    value: orgStats.depts,   color: '#722ed1', icon: <BankOutlined /> },
          { label: '团队数',    value: orgStats.teams,   color: '#fa8c16', icon: <TeamOutlined /> },
        ].map(s => (
          <Col span={6} key={s.label}>
            <Card size="small">
              <Statistic
                title={s.label}
                value={s.value}
                prefix={s.icon}
                valueStyle={{ color: s.color }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Main Card — no default padding */}
      <Card styles={{ body: { padding: 0 } }}>

        {/* Search form */}
        <div style={{ padding: '16px 16px 0', borderBottom: '1px solid #f0f0f0' }}>
          <SearchForm
            fields={searchFields}
            onSearch={setSearchParams}
            onReset={() => setSearchParams({})}
          />
        </div>

        {/* Toolbar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: '1px solid #f0f0f0',
        }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>组织架构管理</span>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenForm('create')}>
              新建组织
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchOrgTree} loading={loading} />
            <Button icon={<ExportOutlined />} onClick={() => message.info('导出功能开发中')} />
          </Space>
        </div>

        {/* Split panel */}
        <div style={{ display: 'flex', minHeight: 560 }}>

          {/* ── Left: Org Tree ── */}
          <div style={{
            width: treeCollapsed ? 0 : 270,
            minWidth: treeCollapsed ? 0 : 270,
            borderRight: '1px solid #f0f0f0',
            display: 'flex',
            flexDirection: 'column',
            background: '#fafafa',
            transition: 'width .2s, min-width .2s',
            overflow: 'hidden',
          }}>
            <Spin spinning={loading && flatOrgs.length === 0}>
              <OrganizationTree
                organizations={flatOrgs}
                loading={false}
                selectedKey={selectedOrg ? String(selectedOrg.id) : undefined}
                expandedKeys={treeExpandedKeys}
                draggable
                showSearch
                showActions
                onSelect={handleTreeSelect}
                onExpand={setTreeExpandedKeys}
                onDrop={handleTreeDrop}
                onEdit={org => handleOpenForm('edit', org)}
                onAddChild={org => handleOpenForm('create', undefined, String(org.id))}
                onDelete={handleDelete}
                style={{ minHeight: 460 }}
              />
            </Spin>
            {/* Tree footer */}
            <div style={{
              padding: '6px 8px',
              borderTop: '1px solid #f0f0f0',
              display: 'flex',
              gap: 6,
              flexShrink: 0,
            }}>
              <Button size="small" style={{ flex: 1 }} onClick={expandAll}>展开全部</Button>
              <Button size="small" style={{ flex: 1 }} onClick={collapseAll}>收起全部</Button>
            </div>
          </div>

          {/* ── Right: Content ── */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>

            {/* collapse toggle button */}
            <Button
              size="small"
              type="text"
              icon={treeCollapsed ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              title={treeCollapsed ? '展开组织树' : '隐藏组织树'}
              onClick={() => setTreeCollapsed(v => !v)}
              style={{ position: 'absolute', top: 10, left: 8, zIndex: 1, color: '#999' }}
            />

            {!selectedOrg ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Empty
                  description="请在左侧选择一个组织查看详情"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </div>
            ) : (
              <>
                {/* Org header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 20px',
                  borderBottom: '1px solid #f0f0f0',
                }}>
                  <Space size={12}>
                    {/* avatar */}
                    <div style={{
                      width: 42, height: 42, borderRadius: 8,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 20,
                      background: typeConf?.bg || '#f5f5f5',
                      color: typeConf?.color || '#666',
                      flexShrink: 0,
                    }}>
                      {typeConf?.icon}
                    </div>
                    <div>
                      <Space size={6} align="center">
                        <span style={{ fontSize: 16, fontWeight: 600 }}>{selectedOrg.name}</span>
                        <Tag color={typeConf?.color} style={{ margin: 0 }}>{typeConf?.text}</Tag>
                        <Tag color={selectedOrg.status === 'active' ? 'success' : 'error'} style={{ margin: 0 }}>
                          {selectedOrg.status === 'active' ? '正常' : '停用'}
                        </Tag>
                      </Space>
                      <div style={{ fontSize: 12, color: '#999', marginTop: 3 }}>
                        编码: {selectedOrg.code}
                        {selectedOrg.leader_name || (selectedOrg as any).leaderName
                          ? ` · 负责人: ${selectedOrg.leader_name || (selectedOrg as any).leaderName}`
                          : ''}
                      </div>
                    </div>
                  </Space>
                  <Space>
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => handleOpenForm('edit', selectedOrg)}
                    >
                      编辑
                    </Button>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => handleOpenForm('create', undefined, String(selectedOrg.id))}
                    >
                      添加子组织
                    </Button>
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      disabled={getOrgType(selectedOrg) === OrganizationType.COMPANY}
                      onClick={() => handleDelete(selectedOrg)}
                    />
                  </Space>
                </div>

                {/* Tabs: 基本信息 / 成员管理 */}
                <Tabs
                  activeKey={activeTab}
                  onChange={key => setActiveTab(key as 'info' | 'members')}
                  items={tabItems}
                  tabBarStyle={{ paddingLeft: 20, marginBottom: 0 }}
                  style={{ flex: 1 }}
                />
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Create / Edit form */}
      <OrganizationForm
        visible={formVisible}
        onClose={() => setFormVisible(false)}
        onSave={handleSaveOrg}
        form={form}
        formMode={formMode}
        orgTreeData={orgTreeData}
      />
    </div>
  );
};

export default OrganizationList;
